import { Component, ElementRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CheckPageAccess } from 'src/app/components/authorize/check-page-access';
import { MyRoleOnboarding } from 'src/app/components/role-onboarding/role-onboarding';
import { resolveVideoEmbed, VideoEmbedType } from 'src/app/components/role-onboarding/video-embed';
import { RoleOnboardingService } from 'src/app/services/role-onboarding.service';
import { ToastService } from 'src/app/services/toast-service.service';

@Component({
    selector: 'app-my-training',
    templateUrl: './my-training.component.html',
    styleUrls: ['./my-training.component.scss'],
    standalone: false
})
export class MyTrainingComponent {
  @ViewChild('detail') detailRef?: ElementRef<HTMLElement>;

  pageName = CheckPageAccess.MYTRAINING;
  courses: MyRoleOnboarding[] = [];
  current: MyRoleOnboarding | null = null;
  isLoading = true;
  acknowledged = false;
  isSubmitting = false;
  embedType: VideoEmbedType | null = null;
  safeEmbedUrl: SafeResourceUrl | null = null;
  videoSrc = '';

  constructor(
    private roleOnboardingService: RoleOnboardingService,
    private toast: ToastService,
    private sanitizer: DomSanitizer) { }

  get acknowledgedCount(): number {
    return this.courses.filter(c => c.isAcknowledged).length;
  }

  get requiredRemaining(): number {
    return this.courses.filter(c => c.isMandatory && !c.isAcknowledged).length;
  }

  get progressPercent(): number {
    return this.courses.length ? Math.round((this.acknowledgedCount / this.courses.length) * 100) : 0;
  }

  get currentIndex(): number {
    return this.current ? this.courses.findIndex(c => c.id === this.current!.id) : -1;
  }

  ngOnInit(): void {
    this.loadCourses();
  }

  loadCourses(selectedId?: number): void {
    this.isLoading = true;
    this.roleOnboardingService.getMine().subscribe({
      next: (data) => {
        this.isLoading = false;
        this.courses = data ?? [];
        const selected = this.courses.find(c => c.id === selectedId)
          ?? this.courses.find(c => !c.isAcknowledged)
          ?? this.courses[0]
          ?? null;
        this.select(selected);
      },
      error: () => {
        this.isLoading = false;
        this.toast.showErrorMessage('Unable to load your training');
      }
    });
  }

  select(course: MyRoleOnboarding | null, scrollToDetail = false): void {
    this.current = course;
    this.acknowledged = false;
    const embed = course ? resolveVideoEmbed(course.videoUrl, this.sanitizer) : null;
    this.embedType = embed?.embedType ?? null;
    this.safeEmbedUrl = embed?.safeEmbedUrl ?? null;
    this.videoSrc = embed?.videoSrc ?? '';

    if (scrollToDetail && window.matchMedia('(max-width: 900px)').matches) {
      setTimeout(() => this.detailRef?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }

  goTo(offset: number): void {
    const next = this.courses[this.currentIndex + offset];
    if (next) {
      this.select(next, true);
    }
  }

  submit(): void {
    if (!this.current || this.current.isAcknowledged || !this.acknowledged || this.isSubmitting) {
      return;
    }

    const courseId = this.current.id;
    this.isSubmitting = true;
    this.roleOnboardingService.acknowledge(courseId).subscribe({
      next: (ok) => {
        this.isSubmitting = false;
        if (ok) {
          this.toast.showMessage('Training acknowledged');
          this.loadCourses(courseId);
        } else {
          this.toast.showErrorMessage('Unable to record acknowledgement');
        }
      },
      error: () => {
        this.isSubmitting = false;
        this.toast.showErrorMessage('Unable to record acknowledgement');
      }
    });
  }
}
